import { useState } from "react";
import { Phone, Mail, Clock } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { Kicker } from "@/components/Bits";
import { Field, inputCls, useLeadSubmit } from "@/components/FormKit";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const empty = { name: "", phone: "", email: "", help_with: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const { submit, submitting } = useLeadSubmit("contact");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    submit(form, () => setForm(empty));
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-40 pb-16 mx-auto max-w-7xl px-5 sm:px-8">
        <Kicker>Contact</Kicker>
        <h1 className="font-display tracking-mega leading-[0.86] text-5xl sm:text-7xl lg:text-8xl mt-6 max-w-4xl">
          <MaskedLines lines={["Let's Talk About", "Your Next Opportunity."]} />
        </h1>
        <p className="mt-8 text-lg sm:text-xl text-foreground/75 max-w-2xl leading-relaxed">
          Have questions about selling your mobile or manufactured home? Reach out to FaeVor Works. We're
          ready to hear where you are and help you explore your options.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-28 grid gap-14 lg:grid-cols-[1fr_1.2fr] lg:gap-24">
        <Reveal>
          <div className="space-y-8">
            {[
              { Icon: Phone, label: "Call", value: "(973) 282-6924", href: "tel:+19732826924" },
              { Icon: Mail, label: "Email", value: "faevorworks@gmail.com", href: "mailto:faevorworks@gmail.com" },
              { Icon: Clock, label: "Hours", value: "Mon–Fri · 9am – 6pm", href: null },
            ].map(({ Icon, label, value, href }) => (
              <div key={label} className="flex items-start gap-5 border-b border-border pb-8">
                <Icon className="text-primary mt-1" size={24} strokeWidth={1.6} />
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-foreground/50">{label}</p>
                  {href ? (
                    <a href={href} className="font-display text-2xl sm:text-3xl tracking-tight hover:text-primary transition-colors">
                      {value}
                    </a>
                  ) : (
                    <p className="font-display text-2xl sm:text-3xl tracking-tight">{value}</p>
                  )}
                </div>
              </div>
            ))}
            <p className="text-sm uppercase tracking-[0.22em] text-primary font-semibold">
              Don't Miss Out on Your FaeVor!
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="rounded-3xl border border-border bg-card p-7 sm:p-10"
          >
            <div className="grid gap-7 sm:grid-cols-2">
              <Field label="Name" htmlFor="ct-name">
                <input id="ct-name" data-testid="ct-name" required className={inputCls}
                  value={form.name} onChange={set("name")} placeholder="Your full name" />
              </Field>
              <Field label="Phone" htmlFor="ct-phone">
                <input id="ct-phone" data-testid="ct-phone" className={inputCls}
                  value={form.phone} onChange={set("phone")} placeholder="(000) 000-0000" />
              </Field>
              <Field label="Email" htmlFor="ct-email">
                <input id="ct-email" data-testid="ct-email" type="email" className={inputCls}
                  value={form.email} onChange={set("email")} placeholder="you@email.com" />
              </Field>
              <Field label="How Can We Help?" htmlFor="ct-help">
                <input id="ct-help" data-testid="ct-help" className={inputCls}
                  value={form.help_with} onChange={set("help_with")} placeholder="Real estate / credit / other" />
              </Field>
            </div>
            <Field label="Message" className="mt-7">
              <Textarea data-testid="ct-message" value={form.message} onChange={set("message")}
                placeholder="Tell us a little about what you're looking for..."
                className="bg-transparent border border-foreground/20 rounded-xl min-h-36 text-base focus-visible:ring-primary" />
            </Field>
            <Button
              type="submit"
              data-testid="ct-submit"
              disabled={submitting}
              className="mt-9 w-full rounded-full bg-primary text-primary-foreground py-6 text-base font-semibold hover:bg-primary/90 hover:scale-[1.01] transition-transform"
            >
              {submitting ? "Sending..." : "Send Message"}
            </Button>
          </form>
        </Reveal>
      </section>
    </div>
  );
}
